import React, { useEffect, useState } from "react";
import { usePlayer } from "@empirica/core/player/classic/react";
import { Button } from "../components/Button.jsx";
import { savePlayerMeta } from "../utils/savePlayerMeta.js";
import { NO_SPINNER_CLASS, typedNumberInputProps } from "../utils/numberInput.js";

const QUESTION_IDS = [
  "disease-screening",
  "simpsons-paradox",
  "monty-hall",
  "flat-tax",
];

const QUESTION_BANK = {
  "disease-screening": {
    id: "disease-screening",
    shortTitle: "Disease Screening",
    title: "Question: Disease Screening",
    answerType: "numeric",
    answerLabel:
      "What is the probability that this person actually has Veltman syndrome?",
    answerPlaceholder: "Enter a percentage from 0 to 100",
    answerSuffix: "%",
    answerMin: 0,
    answerMax: 100,
    confidenceMin: 0,
    confidenceMax: 100,
    renderPrompt() {
      return (
        <div className="space-y-4 text-gray-800">
          <p>
            A blood disorder called Veltman syndrome affects{" "}
            <strong>10%</strong> of the adult population in a certain region. A
            new screening test has been developed to detect Veltman syndrome.
            Clinical trials have established the following about the test:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              If a person has Veltman syndrome, the test will correctly return a
              positive result 90% of the time.
            </li>
            <li>
              If a person does not have Veltman syndrome, the test will
              incorrectly return a positive result 40% of the time.
            </li>
          </ul>
          <p>
            A randomly selected adult from this region takes the test and
            receives a positive result.
          </p>
        </div>
      );
    },
  },
  "simpsons-paradox": {
    id: "simpsons-paradox",
    shortTitle: "Treatment Effectiveness",
    title: "Question: Treatment Effectiveness",
    answerType: "choice",
    answerLabel:
      "Based on these data, which treatment is more effective at helping patients recover?",
    options: [
      { value: "Treatment A", label: "Treatment A" },
      { value: "Treatment B", label: "Treatment B" },
    ],
    confidenceMin: 50,
    confidenceMax: 100,
    renderPrompt() {
      return (
        <div className="space-y-4 text-gray-800">
          <p>
            A hospital is evaluating two treatments for a respiratory infection.
            The hospital compiled recovery data for 700 patients, organized by
            the severity of the infection at the time of admission.
          </p>

          <div className="space-y-5">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Treatment A (350 patients)
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-200 px-3 py-2 text-left">
                        Patient Group
                      </th>
                      <th className="border border-gray-200 px-3 py-2 text-right">
                        Recovered
                      </th>
                      <th className="border border-gray-200 px-3 py-2 text-right">
                        Total
                      </th>
                      <th className="border border-gray-200 px-3 py-2 text-right">
                        Recovery Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">
                        Overall
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        273
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        350
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        78.0%
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">
                        Mild cases
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        81
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        87
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        93.1%
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">
                        Severe cases
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        192
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        263
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        73.0%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Treatment B (350 patients)
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-200 px-3 py-2 text-left">
                        Patient Group
                      </th>
                      <th className="border border-gray-200 px-3 py-2 text-right">
                        Recovered
                      </th>
                      <th className="border border-gray-200 px-3 py-2 text-right">
                        Total
                      </th>
                      <th className="border border-gray-200 px-3 py-2 text-right">
                        Recovery Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">
                        Overall
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        289
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        350
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        82.6%
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">
                        Mild cases
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        234
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        270
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        86.7%
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">
                        Severe cases
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        55
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        80
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right">
                        68.8%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      );
    },
  },
  "monty-hall": {
    id: "monty-hall",
    shortTitle: "Monty Hall",
    title: "Question: Monty Hall",
    answerType: "choice",
    answerLabel:
      "Should you stay with Door 1 or switch to Door 2 to give yourself the best chance of winning the car?",
    options: [
      { value: "Stay with Door 1", label: "Stay with Door 1" },
      { value: "Switch to Door 2", label: "Switch to Door 2" },
      {
        value: "It is indifferent",
        label: "Choose either Door 1 or Door 2",
      },
    ],
    confidenceMin: 33,
    confidenceMax: 100,
    renderPrompt() {
      return (
        <div className="space-y-4 text-gray-800">
          <p>
            Imagine you are a contestant on a game show. The host presents you
            with three closed doors: Door 1, Door 2, and Door 3. Behind one of
            the doors is a new car. Behind each of the other two doors is a
            goat. You do not know which door hides the car.
          </p>
          <div>
            <p className="font-semibold text-gray-900 mb-2">
              The rules of the game are as follows:
            </p>
            <ol className="list-decimal pl-6 space-y-1">
              <li>You choose one of the three doors.</li>
              <li>
                The host, who knows what is behind every door, then opens one of
                the two doors you did not choose. The host will always open a
                door that has a goat behind it.
              </li>
              <li>
                After the host opens a door, you are given the option to either
                stay with your original choice or switch to the other unopened
                door.
              </li>
            </ol>
          </div>
          <p>
            You choose Door 1. The host opens Door 3, revealing a goat.
          </p>
        </div>
      );
    },
  },
  "flat-tax": {
    id: "flat-tax",
    shortTitle: "Flat Tax Reform",
    title: "Question: Flat Tax Reform",
    answerType: "choice",
    answerLabel:
      "What happened to total personal income tax revenue in the first full year after the reform?",
    options: [
      { value: "Increase", label: "Increase" },
      { value: "Decrease", label: "Decrease" },
      { value: "Stay the same", label: "Stay the same" },
    ],
    confidenceMin: 33,
    confidenceMax: 100,
    renderPrompt() {
      return (
        <div className="space-y-4 text-gray-800">
          <p>
            A large country with a developing economy recently transitioned from
            a centrally planned system to a market economy. It has been taxing
            personal income under a progressive system for several years.
          </p>
          <p>
            Personal income tax is collected through a mix of employer
            withholding and self-reporting. For most salaried workers,
            compliance is relatively high because income is recorded in payroll
            systems. For some higher-income individuals and business owners,
            however, a substantial share of income comes from sources that are
            harder to verify through payroll alone.
          </p>
          <p>
            The tax authority has limited administrative capacity. Some
            employers and financial institutions provide third-party records, but
            coverage is uneven outside the formal payroll sector. Audits exist,
            but they are selective and do not reach most taxpayers in a typical
            year. The economy is growing at a moderate pace, and there are no
            major macroeconomic crises anticipated at the time of the reform.
          </p>
          <p>
            The government then announces a major reform. Beginning at the start
            of the next tax year, the old progressive rate structure will be
            replaced by a single flat tax. Relative to the old system, the
            statutory tax rate rises for lower-income taxpayers, stays the same
            for middle-income taxpayers, and falls for higher-income taxpayers.
          </p>
          <p>
            Assume the country has exactly three taxpayer groups and the
            following totals. All dollar figures below are each group&apos;s total{" "}
            <strong>actual income</strong> in a given year.
          </p>
          <div className="space-y-3">
            <div>
              <p className="font-semibold text-gray-900">Before the reform:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  Lower-income taxpayers earn <strong>$100 million</strong> in
                  total. All of that income is reported for tax purposes. Their
                  tax rate is <strong>10%</strong>.
                </li>
                <li>
                  Middle-income taxpayers earn <strong>$100 million</strong> in
                  total. All of that income is reported for tax purposes. Their
                  tax rate is <strong>20%</strong>.
                </li>
                <li>
                  Higher-income taxpayers earn <strong>$200 million</strong> in
                  total. Only half of that income is reported for tax purposes.
                  Their tax rate is <strong>30%</strong>.
                </li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-gray-900">After the reform:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  The country replaces those three rates with a{" "}
                  <strong>flat tax of 20%</strong> on all reported income.
                </li>
                <li>Each group&apos;s actual income stays exactly the same as before.</li>
                <li>Lower-income taxpayers report all of their income.</li>
                <li>Middle-income taxpayers report all of their income.</li>
                <li>Higher-income taxpayers report all of their income.</li>
              </ul>
            </div>
          </div>
          <p>
            There are no deductions, exemptions, credits, or other taxes to
            consider.
          </p>
        </div>
      );
    },
  },
};

function isValidQuestionOrder(value) {
  return (
    Array.isArray(value) &&
    value.length === QUESTION_IDS.length &&
    QUESTION_IDS.every((questionId) => value.includes(questionId))
  );
}

function shuffleQuestionIds() {
  const ids = [...QUESTION_IDS];
  for (let index = ids.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [ids[index], ids[swapIndex]] = [ids[swapIndex], ids[index]];
  }
  return ids;
}

function getConfidenceHelpText(question) {
  if (question.confidenceMin === 50) {
    return "Confidence means the probability that your chosen answer is correct. Because this question has two answer options, confidence must be a whole number from 50 to 100.";
  }
  if (question.confidenceMin === 33) {
    return "Confidence means the probability that your chosen answer is correct. Because this question has three answer options, confidence must be a whole number from 33 to 100.";
  }
  return "Confidence means the probability that your chosen answer is correct. Enter a whole number from 0 to 100.";
}

function getConfidenceErrorText(question) {
  if (question.confidenceMin === 50) {
    return "Please enter a whole-number confidence from 50 to 100. Values below 50 are not allowed once you have chosen one of two options.";
  }
  if (question.confidenceMin === 33) {
    return "Please enter a whole-number confidence from 33 to 100. Values below 33 are not allowed once you have chosen one of three options.";
  }
  return "Please enter a whole-number confidence from 0 to 100.";
}

function validateAnswer(question, answer) {
  if (question.answerType === "choice") {
    if (!answer) {
      return "Please select an answer before continuing.";
    }
    const isValidOption = (question.options || []).some(
      (option) => option.value === answer
    );
    if (!isValidOption) {
      return "Please select one of the listed answer options.";
    }
    return null;
  }

  if (String(answer || "").trim() === "") {
    return "Please enter your answer before continuing.";
  }

  const parsedAnswer = Number(answer);
  if (!Number.isFinite(parsedAnswer)) {
    return "Please enter a numeric answer.";
  }

  if (
    Number.isFinite(question.answerMin) &&
    parsedAnswer < question.answerMin
  ) {
    return `Please enter a numeric answer of at least ${question.answerMin}.`;
  }

  if (
    Number.isFinite(question.answerMax) &&
    parsedAnswer > question.answerMax
  ) {
    return `Please enter a numeric answer of at most ${question.answerMax}.`;
  }

  return null;
}

function validateConfidence(question, confidence) {
  if (String(confidence || "").trim() === "") {
    return getConfidenceErrorText(question);
  }

  const parsedConfidence = Number(confidence);
  const wholeNumberConfidence = Number.isInteger(parsedConfidence);

  if (
    !Number.isFinite(parsedConfidence) ||
    !wholeNumberConfidence ||
    parsedConfidence < question.confidenceMin ||
    parsedConfidence > question.confidenceMax
  ) {
    return getConfidenceErrorText(question);
  }

  return null;
}

function QuestionOptions({ options, selectedValue, onChange }) {
  return (
    <div className="grid gap-3">
      {options.map((option) => (
        <label
          key={option.value}
          className={`border rounded-lg px-4 py-3 cursor-pointer ${
            selectedValue === option.value
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200 bg-white"
          }`}
        >
          <input
            type="radio"
            name="four-task-answer"
            value={option.value}
            checked={selectedValue === option.value}
            onChange={(event) => onChange(event.target.value)}
            className="mr-3"
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

export function FourTaskPilotTask({ next }) {
  const player = usePlayer();
  const [questionOrder] = useState(() => {
    const existingOrder = player.get("fourTaskQuestionOrder");
    return isValidQuestionOrder(existingOrder)
      ? existingOrder
      : shuffleQuestionIds();
  });
  const [responses, setResponses] = useState(() => {
    const existingResponses = player.get("fourTaskResponses");
    return existingResponses && typeof existingResponses === "object"
      ? existingResponses
      : {};
  });
  const [currentIndex, setCurrentIndex] = useState(() => {
    const existingIndex = Number(player.get("fourTaskCurrentIndex"));
    return Number.isInteger(existingIndex) &&
      existingIndex >= 0 &&
      existingIndex < QUESTION_IDS.length
      ? existingIndex
      : 0;
  });
  const [answer, setAnswer] = useState("");
  const [confidence, setConfidence] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const currentQuestionId = questionOrder[currentIndex];
  const currentQuestion = QUESTION_BANK[currentQuestionId];

  useEffect(() => {
    player.set("fourTaskQuestionOrder", questionOrder);
  }, [player, questionOrder]);

  useEffect(() => {
    player.set("fourTaskCurrentIndex", currentIndex);
  }, [player, currentIndex]);

  useEffect(() => {
    const currentResponse = responses[currentQuestionId] || {};
    setAnswer(
      currentResponse.answer === undefined || currentResponse.answer === null
        ? ""
        : String(currentResponse.answer)
    );
    setConfidence(
      currentResponse.confidence === undefined ||
        currentResponse.confidence === null
        ? ""
        : String(currentResponse.confidence)
    );
    setErrorMessage("");
  }, [responses, currentQuestionId]);

  useEffect(() => {
    function blockClipboardActions(event) {
      const key = String(event.key || "").toLowerCase();
      const usingModifier = event.metaKey || event.ctrlKey;
      const blockedShortcut =
        usingModifier && (key === "c" || key === "v" || key === "x");

      if (
        event.type === "copy" ||
        event.type === "cut" ||
        event.type === "paste" ||
        event.type === "contextmenu" ||
        event.type === "dragstart" ||
        blockedShortcut
      ) {
        event.preventDefault();
      }
    }

    document.addEventListener("copy", blockClipboardActions, true);
    document.addEventListener("cut", blockClipboardActions, true);
    document.addEventListener("paste", blockClipboardActions, true);
    document.addEventListener("contextmenu", blockClipboardActions, true);
    document.addEventListener("dragstart", blockClipboardActions, true);
    document.addEventListener("keydown", blockClipboardActions, true);

    return () => {
      document.removeEventListener("copy", blockClipboardActions, true);
      document.removeEventListener("cut", blockClipboardActions, true);
      document.removeEventListener("paste", blockClipboardActions, true);
      document.removeEventListener("contextmenu", blockClipboardActions, true);
      document.removeEventListener("dragstart", blockClipboardActions, true);
      document.removeEventListener("keydown", blockClipboardActions, true);
    };
  }, []);

  function handleSubmit() {
    const answerError = validateAnswer(currentQuestion, answer);
    if (answerError) {
      setErrorMessage(answerError);
      return;
    }

    const confidenceError = validateConfidence(currentQuestion, confidence);
    if (confidenceError) {
      setErrorMessage(confidenceError);
      return;
    }

    const timestamp = Date.now();
    const responseRecord = {
      questionId: currentQuestion.id,
      questionTitle: currentQuestion.shortTitle,
      displayOrder: currentIndex + 1,
      answer:
        currentQuestion.answerType === "numeric" ? Number(answer) : answer,
      confidence: Number(confidence),
      submittedAt: timestamp,
    };

    const nextResponses = {
      ...responses,
      [currentQuestion.id]: responseRecord,
    };

    setResponses(nextResponses);
    player.set("fourTaskResponses", nextResponses);

    if (currentIndex === questionOrder.length - 1) {
      const payload = {
        order: questionOrder,
        responses: questionOrder
          .map((questionId) => nextResponses[questionId])
          .filter(Boolean),
        completedAt: timestamp,
      };

      player.set("fourTaskPilot", payload);
      player.set("fourTaskCompleted", true);
      player.set("fourTaskCompletedAt", timestamp);
      player.set("readyToExit", true);

      savePlayerMeta({
        player,
        name: "fourTaskPilot",
        value: payload,
        debugTag: "Four Task Pilot",
      });

      next();
      return;
    }

    setCurrentIndex((previous) => previous + 1);
  }

  if (!currentQuestion) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">
            The question set could not be loaded. Please contact the
            researcher.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-red-900">
          This is part of an experiment. Please work on your own. Do not use AI
          systems or other outside help. Copying and pasting is disabled during
          this task, and responses may be checked for likely prohibited tool
          use.
        </p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">{currentQuestion.title}</h2>
        <p className="text-sm text-gray-600">
          Question {currentIndex + 1} of {questionOrder.length}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6 select-none">
        <div className="space-y-4">{currentQuestion.renderPrompt()}</div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <label className="block text-lg font-semibold mb-3">
          {currentQuestion.answerLabel}
        </label>

        {currentQuestion.answerType === "choice" ? (
          <QuestionOptions
            options={currentQuestion.options || []}
            selectedValue={answer}
            onChange={(value) => {
              setAnswer(value);
              setErrorMessage("");
            }}
          />
        ) : (
          <div>
            <input
              className={`w-full border border-gray-300 rounded-md p-3 ${NO_SPINNER_CLASS}`}
              type="number"
              step="any"
              placeholder={currentQuestion.answerPlaceholder}
              value={answer}
              onChange={(event) => {
                setAnswer(event.target.value);
                setErrorMessage("");
              }}
              {...typedNumberInputProps()}
            />
            {currentQuestion.answerSuffix && (
              <p className="text-sm text-gray-500 mt-2">
                Enter your answer in {currentQuestion.answerSuffix}.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <label className="block text-lg font-semibold mb-2">
          Confidence
        </label>
        <p className="text-sm text-gray-600 mb-3">
          {getConfidenceHelpText(currentQuestion)}
        </p>
        <input
          className={`w-full border border-gray-300 rounded-md p-3 ${NO_SPINNER_CLASS}`}
          type="number"
          min={currentQuestion.confidenceMin}
          max={currentQuestion.confidenceMax}
          step="1"
          placeholder={`${currentQuestion.confidenceMin}-${currentQuestion.confidenceMax}`}
          value={confidence}
          onChange={(event) => {
            setConfidence(event.target.value);
            setErrorMessage("");
          }}
          {...typedNumberInputProps()}
        />
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center">
          <Button
            handleClick={handleSubmit}
            className="px-8 py-3 bg-green-500 hover:bg-green-600"
          >
            {currentIndex === questionOrder.length - 1
              ? "Finish Questions"
              : "Next Question"}
          </Button>
        </div>
      </div>
    </div>
  );
}
